import { v4 as uuidv4 } from 'uuid';
import { 
  ITranscriptContainer,
  ISegment,
  IParagraph,
  ITextElement,
  IPunctElement
} from '../models/interfaces';

import { 
  TranscriptContainer,
  Segment,
  Paragraph 
} from '../models';

// Import types from app-wide definitions
import { TranscriptElement as AppTranscriptElement } from '@src/types.ts/transcript';
import { TranscriptElementFactory } from '../models/TranscriptElementFactory';

// Import legacy types from separate file
import {
  LegacyTranscriptTextElement,
  LegacyTranscriptPunctElement,
  LegacyTranscriptData
} from '../models/legacyTypes';

// Define a more specific type for elements from external transcript API
export interface TranscriptElement {
  id: string;
  type: 'text' | 'punct';
  value: string | null;
  old_value?: string | null;
  ts?: number | null;
  end_ts?: number | null;
  initial_index?: number;
  speaker_label?: string;
  segment_id?: string;
  paragraph_id?: string;
  color?: string; // Add explicit color field
  [key: string]: any; // Allow additional properties
}

/**
 * LegacyConverter utility class for converting between the new transcript model
 * and legacy transcript formats used by other parts of the application
 */
export class LegacyConverter {
  /**
   * Adapts transcript elements from the application-wide type to the local converter type
   * This makes it easier to work with different element types while maintaining type safety
   * 
   * @param elements Array of app-wide transcript elements
   * @returns Array of converter-compatible transcript elements
   */
  static adaptTranscriptElements(elements: AppTranscriptElement[]): TranscriptElement[] {
    return elements.map(element => {
      // Create a new object that matches the converter's required structure
      const adapted: TranscriptElement = {
        id: element.id || `element_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        type: element.type,
        value: element.value,
        old_value: element.old_value,
        ts: element.ts,
        end_ts: element.end_ts,
        initial_index: element.initial_index,
        speaker_label: element.speaker_label,
        segment_id: element.segment_id,
        paragraph_id: element.paragraph_id,
        state: element.state,
        color: element.color // Include the color field
      };
      
      return adapted;
    });
  }

  /**
   * Converts an external transcript element to internal model
   * 
   * @param element External transcript element
   * @returns Internal transcript element model
   */
  static toTranscriptElement(element: TranscriptElement): ITextElement | IPunctElement {
    if (element.type === 'text') {
      return TranscriptElementFactory.createTextElement(
        element.id || `element_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        element.value || '',
        element.ts || undefined,
        element.end_ts || undefined,
        1.0, // Default confidence
        element.color // Include the color field
      );
    } else {
      return TranscriptElementFactory.createPunctElement(
        element.id || `element_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        element.value || '',
        element.color // Include the color field
      );
    }
  }

  /**
   * Adapts a transcript output to make it compatible with the app-wide TranscriptData type
   * 
   * @param input Transcript output from containerToLegacyTranscript
   * @returns Object compatible with TranscriptData type
   */
  static adaptToTranscriptData(input: {
    fullText: string;
    version: number;
    elements: TranscriptElement[];
    language: string;
  }): import('@src/types.ts/transcript').TranscriptData {
    // Convert all elements to the app-wide TranscriptElement type
    return {
      fullText: input.fullText,
      version: input.version,
      language: input.language,
      elements: input.elements as unknown as AppTranscriptElement[]
    };
  }

  /**
   * Converts a TranscriptContainer instance to the legacy format expected by other parts of the app
   * 
   * @param container TranscriptContainer instance to convert
   * @returns TranscriptData in external format
   */
  static containerToLegacyTranscript(container: ITranscriptContainer): {
    fullText: string;
    version: number;
    elements: TranscriptElement[];
    language: string;
  } {
    const outputElements: TranscriptElement[] = [];
    let initialIndex = 0;
    
    // Process each segment
    for (const segment of container.segments) {
      // Process each paragraph in the segment
      for (const paragraph of segment.paragraphs) {
        // Process each element in the paragraph
        for (const element of paragraph.elements) {
          if (element.type === 'text') {
            // Convert text element
            const textElement = element as ITextElement;
            const outputElement: TranscriptElement = {
              id: textElement.id,
              type: 'text',
              value: textElement.value,
              old_value: null,
              ts: textElement.startTime || 0, // Provide default value for ts
              end_ts: textElement.endTime || 0, // Provide default value for end_ts
              initial_index: initialIndex++,
              speaker_label: segment.speakerLabel,
              segment_id: segment.id,
              paragraph_id: paragraph.id,
              color: textElement.color // Include the color field
            };
            outputElements.push(outputElement);
          } else {
            // Convert punctuation element
            const punctElement = element as IPunctElement;
            const outputElement: TranscriptElement = {
              id: punctElement.id,
              type: 'punct',
              value: punctElement.value,
              old_value: null,
              initial_index: initialIndex++,
              ts: null, // Required by TranscriptPunctElement
              end_ts: null, // Required by TranscriptPunctElement
              color: punctElement.color // Include the color field
            };
            outputElements.push(outputElement);
          }
        }
      }
    }
    
    return {
      fullText: this.getFullTranscriptText(container),
      version: container.version,
      elements: outputElements,
      language: container.language
    };
  }
  
  /**
   * Converts a legacy transcript to the new container format
   * 
   * @param elements Array of legacy TranscriptElements
   * @param version Transcript version
   * @param storyId ID of the story
   * @param language Language code for the transcript
   * @returns A new TranscriptContainer instance
   */
  static legacyTranscriptToContainer(
    elements: TranscriptElement[],
    version: number,
    storyId: string,
    language: string = 'en-us'
  ): TranscriptContainer {
    // Generate a unique ID for the container
    const containerId = uuidv4();
    
    // Create an empty container
    const container = new TranscriptContainer(containerId, storyId, version, language);

    // Guard against empty input
    if (!elements || elements.length === 0) {
      return container;
    }

    let currentSegmentId = '';
    let currentParagraphId = '';

    // Initialize with the first element's speaker label (or empty if none)
    let currentSegment = new Segment(
      uuidv4(), 
      elements[0].speaker_label || '', 
      -1, 
      -1, 
      []
    );
    
    let currentParagraph = new Paragraph(
      uuidv4(), 
      -1, 
      -1, 
      [], 
      currentSegmentId
    );  

    // Process each element
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      
      // Skip removed or cut elements
      if (element.state === 'removed' || element.state === 'cut') {
        continue;
      }
      
      // Handle segment change
      if (element.segment_id && element.segment_id !== currentSegmentId) {
        // Add the current paragraph to the current segment
        if (currentParagraph.elements.length > 0 && currentParagraph.elements.some(e => e.type === 'text')) {
          currentSegment.addParagraph(currentParagraph);
        }

        // Avoid pushing empty segments at the start
        if (currentSegmentId !== '') {
          container.addSegment(currentSegment);
        }
        currentSegmentId = element.segment_id;
        currentSegment = new Segment(
          currentSegmentId, 
          element.speaker_label || '', 
          -1, 
          -1, 
          []
        );

        currentParagraphId = element.paragraph_id || '';
        currentParagraph = new Paragraph(
          currentParagraphId, 
          -1, 
          -1, 
          [], 
          currentSegmentId
        );
      }
      
      // Handle paragraph change
      if (element.paragraph_id && element.paragraph_id !== currentParagraphId) {
        // Avoid pushing empty paragraphs at the start
        if (currentParagraphId !== '') {
          currentSegment.addParagraph(currentParagraph);
        }
        currentParagraphId = element.paragraph_id;
        currentParagraph = new Paragraph(
          currentParagraphId, 
          -1, 
          -1, 
          [], 
          currentSegmentId
        );
      }
      
      // Update segment time info
      if (element.type === 'text' && currentSegment.startTime === -1 && element.ts !== null) {
        currentSegment.startTime = element.ts ?? 0;
      }
      
      if (element.type === 'text' && element.end_ts !== null) {
        currentSegment.endTime = element.end_ts ?? 0;
      }
      
      // Update paragraph time info
      if (element.type === 'text' && currentParagraph.startTime === -1 && element.ts !== null) {
        currentParagraph.startTime = element.ts ?? 0;
      }
      
      if (element.type === 'text' && element.end_ts !== null) {
        currentParagraph.endTime = element.end_ts ?? 0;
      }
      
      // Convert and add the element to the current paragraph
      currentParagraph.addElement(this.toTranscriptElement(element));
    }
    
    // Add the final segment and paragraph if they contain elements
    if (currentParagraph.elements.length > 0 && currentParagraph.elements.some(e => e.type === 'text')) {
      currentSegment.addParagraph(currentParagraph);
    }
    
    if (currentSegment.paragraphs.length > 0) {
      container.addSegment(currentSegment);
    }
    
    return container;
  }
  
  /**
   * Gets the full transcript text from a container
   * 
   * @param container TranscriptContainer instance
   * @returns Full transcript text
   */
  static getFullTranscriptText(container: ITranscriptContainer): string {
    let text = '';
    
    for (const segment of container.segments) {
      for (const paragraph of segment.paragraphs) {
        text += paragraph.getText();
      }
    }
    
    return text;
  }
  
  /**
   * Converts a container to a legacy TranscriptData object
   * 
   * @param container TranscriptContainer instance
   * @returns Legacy TranscriptData object
   */
  static containerToLegacyTranscriptData(container: ITranscriptContainer): LegacyTranscriptData {
    const legacyElements: Array<LegacyTranscriptTextElement | LegacyTranscriptPunctElement> = [];
    let initialIndex = 0;
    
    // Process each segment
    for (const segment of container.segments) {
      // Process each paragraph in the segment
      for (const paragraph of segment.paragraphs) {
        // Process each element in the paragraph
        for (const element of paragraph.elements) {
          if (element.type === 'text') {
            // Convert text element
            const textElement = element as ITextElement;
            const legacyElement: LegacyTranscriptTextElement = {
              id: textElement.id,
              type: 'text',
              value: textElement.value,
              value_normalized: textElement.value.toLowerCase().trim(),
              ts: textElement.startTime || 0, // Provide default value for ts
              end_ts: textElement.endTime || 0, // Provide default value for end_ts
              word_count: textElement.value.split(/\s+/).length,
              initial_index: initialIndex++,
              speaker_label: segment.speakerLabel,
              confidence: (textElement as ITextElement).confidence || 1.0,
              segment_id: segment.id,
              paragraph_id: paragraph.id,
              old_value: "", // Include required old_value property
              color: textElement.color // Include the color field
            };
            legacyElements.push(legacyElement);
          } else {
            // Convert punctuation element
            const punctElement = element as IPunctElement;
            const legacyElement: LegacyTranscriptPunctElement = {
              id: punctElement.id,
              type: 'punct',
              value: punctElement.value,
              value_normalized: punctElement.value,
              initial_index: initialIndex++,
              ts: null, // Required by LegacyTranscriptPunctElement
              end_ts: null, // Required by LegacyTranscriptPunctElement
              old_value: "", // Include required old_value property
              color: punctElement.color // Include the color field
            };
            legacyElements.push(legacyElement);
          }
        }
      }
    }
    
    return {
      fullText: this.getFullTranscriptText(container),
      elements: legacyElements,
      language: container.language
    };
  }
}